import './i18n-hints.css';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import {
  loadLocale,
  normalizeLocale,
  saveLocale,
  type UiLocale,
} from '../lib/localePrefs';

import enCommon from './locales/en/common.json';
import enLogin from './locales/en/login.json';
import enNav from './locales/en/nav.json';
import enErrors from './locales/en/errors.json';
import enChrome from './locales/en/chrome.json';
import enFtue from './locales/en/ftue.json';
import enPerformance from './locales/en/performance.json';
import enHorizon from './locales/en/horizon.json';
import enGrowth from './locales/en/growth.json';
import enLive from './locales/en/live.json';
import enSettings from './locales/en/settings.json';
import enAssistant from './locales/en/assistant.json';
import enMetrics from './locales/en/metrics.json';
import enHotel from './locales/en/hotel.json';

import deCommon from './locales/de/common.json';
import deLogin from './locales/de/login.json';
import deNav from './locales/de/nav.json';
import deErrors from './locales/de/errors.json';
import deChrome from './locales/de/chrome.json';
import deFtue from './locales/de/ftue.json';
import dePerformance from './locales/de/performance.json';
import deHorizon from './locales/de/horizon.json';
import deGrowth from './locales/de/growth.json';
import deLive from './locales/de/live.json';
import deSettings from './locales/de/settings.json';
import deAssistant from './locales/de/assistant.json';
import deMetrics from './locales/de/metrics.json';
import deHotel from './locales/de/hotel.json';

export const I18N_NAMESPACES = [
  'common',
  'login',
  'nav',
  'errors',
  'chrome',
  'ftue',
  'performance',
  'horizon',
  'growth',
  'live',
  'settings',
  'assistant',
  'metrics',
  'hotel',
] as const;

const resources = {
  en: {
    common: enCommon,
    login: enLogin,
    nav: enNav,
    errors: enErrors,
    chrome: enChrome,
    ftue: enFtue,
    performance: enPerformance,
    horizon: enHorizon,
    growth: enGrowth,
    live: enLive,
    settings: enSettings,
    assistant: enAssistant,
    metrics: enMetrics,
    hotel: enHotel,
  },
  de: {
    common: deCommon,
    login: deLogin,
    nav: deNav,
    errors: deErrors,
    chrome: deChrome,
    ftue: deFtue,
    performance: dePerformance,
    horizon: deHorizon,
    growth: deGrowth,
    live: deLive,
    settings: deSettings,
    assistant: deAssistant,
    metrics: deMetrics,
    hotel: deHotel,
  },
};

export function applyDocumentLocale(locale: UiLocale): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = locale;
  const title = i18n.t('common:documentTitle');
  if (title) document.title = title;
}

export async function changeLocale(locale: UiLocale): Promise<void> {
  const next = normalizeLocale(locale);
  saveLocale(next);
  await i18n.changeLanguage(next);
  applyDocumentLocale(next);
}

export function currentLocale(): UiLocale {
  return normalizeLocale(i18n.language);
}

/** Locale header + BCP 47 tag for every API fetch. */
export function localeHeaders(): Record<string, string> {
  return { 'Accept-Language': currentLocale() };
}

if (!i18n.isInitialized) {
  const initial = loadLocale();
  void i18n.use(initReactI18next).init({
    resources,
    lng: initial,
    fallbackLng: 'en',
    supportedLngs: ['en', 'de'],
    defaultNS: 'common',
    ns: [...I18N_NAMESPACES],
    interpolation: { escapeValue: false },
    returnNull: false,
    react: { useSuspense: false },
  });
  applyDocumentLocale(initial);
}

export default i18n;
